import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  Chip,
  Collapse,
  Divider,
  Tooltip,
  useTheme
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Highlight as HighlightIcon
} from '@mui/icons-material';
import './SearchHighlight.css';
import { viewportStateManager } from './viewportStateManager';
import { useTranslation } from 'react-i18next';

export interface SearchTerm {
  id: string;
  value: string;
  addedAt: Date;
}

interface SearchHighlightPanelProps {
  searchTerms: SearchTerm[];
  onAddSearchTerm: (term: string) => void;
  onRemoveSearchTerm: (id: string) => void;
  onClearAllSearchTerms: () => void;
  isMinimized: boolean;
  onToggleMinimize: () => void;
  isFullscreen: boolean;
  networkRef?: React.RefObject<any>; // Added for viewport capture
}

const SearchHighlightPanel: React.FC<SearchHighlightPanelProps> = ({
  searchTerms,
  onAddSearchTerm,
  onRemoveSearchTerm,
  onClearAllSearchTerms,
  isMinimized,
  onToggleMinimize,
  isFullscreen,
  networkRef
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [currentInput, setCurrentInput] = useState('');

  const handleAddTerm = () => {
    if (currentInput.trim()) {
      // Capture viewport BEFORE adding search terms
      if (networkRef?.current) {
        viewportStateManager.captureViewport(networkRef.current);
      }
      
      // Split by comma and add each term
      const terms = currentInput.split(',').map(term => term.trim()).filter(term => term.length > 0);
      terms.forEach(term => {
        // Avoid duplicates
        if (!searchTerms.some(existing => existing.value.toLowerCase() === term.toLowerCase())) {
          onAddSearchTerm(term);
        }
      });
      setCurrentInput('');
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleAddTerm();
    }
  };

  const handleRemoveChip = (id: string) => {
    onRemoveSearchTerm(id);
  };

  return (
    <Paper
      elevation={6}
      data-search-panel="true"
      className="search-highlight-panel"
      sx={{
        position: 'absolute',
        top: 10,
        right: 10,
        width: isMinimized ? 'auto' : 320,
        maxWidth: isFullscreen ? '400px' : '350px',
        zIndex: 1000,
        backgroundColor: isDarkMode ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(8px) saturate(180%)',
        border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
        borderRadius: 2,
        overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 0, 0, 0.05)',
        transform: 'scale(0.75)',
        transformOrigin: 'top right'
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1.5,
          backgroundColor: isDarkMode ? 'rgba(144, 202, 249, 0.16)' : 'rgba(25, 118, 210, 0.1)',
          borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
          cursor: 'pointer',
          transition: 'background-color 0.2s ease',
          '&:hover': {
            backgroundColor: isDarkMode ? 'rgba(144, 202, 249, 0.24)' : 'rgba(25, 118, 210, 0.15)',
          }
        }}
        onClick={onToggleMinimize}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HighlightIcon color="primary" fontSize="small" />
          <Typography variant="subtitle2" fontWeight="bold">
            {t('graph.search')}
          </Typography>
          {searchTerms.length > 0 && !isMinimized && (
            <Chip 
              label={searchTerms.length} 
              size="small" 
              color="primary" 
              variant="outlined"
            />
          )}
        </Box>
        <IconButton size="small">
          {isMinimized ? <ExpandMoreIcon /> : <ExpandLessIcon />}
        </IconButton>
      </Box>

      {/* Content */}
      <Collapse in={!isMinimized}>
        <Box sx={{ p: 2 }}>
          {/* Search Input */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              size="small"
              fullWidth
              placeholder={t('graph.searchTerm')}
              value={currentInput}
              onChange={(e) => setCurrentInput(e.target.value)}
              onKeyPress={handleKeyPress}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)' }} />,
                sx: { 
                  fontSize: '0.875rem',
                  color: isDarkMode ? '#ffffff' : '#000000',
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                    color: isDarkMode ? '#ffffff' : '#000000',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(25, 118, 210, 0.5)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#1976d2',
                      borderWidth: '2px',
                    }
                  },
                  '& .MuiInputBase-input': {
                    color: isDarkMode ? '#ffffff' : '#000000',
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.6)',
                    opacity: 1,
                  }
                }
              }}
            />
            <Tooltip title={t('graph.addSearchTerms')}>
              <IconButton 
                size="small" 
                onClick={handleAddTerm}
                disabled={!currentInput.trim()}
                color="primary"
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Active Search Terms */}
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold">
                {t('graph.activeTerms', { count: searchTerms.length })}
              </Typography>
              {searchTerms.length > 0 && (
                <Tooltip title={t('graph.clearSearchTerms')}>
                  <IconButton size="small" onClick={onClearAllSearchTerms} color="error">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, minHeight: 40 }}>
              {searchTerms.length === 0 ? (
                <Typography 
                  variant="body2" 
                  sx={{ 
                    py: 1,
                    color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)'
                  }}
                >
                  {t('graph.noSearchTerms')}
                </Typography>
              ) : (
                searchTerms.map((term) => (
                  <Chip
                    key={term.id}
                    label={term.value}
                    onDelete={() => handleRemoveChip(term.id)}
                    size="small"
                    color="primary"
                    variant="outlined"
                    className="search-term-chip"
                    sx={{
                      backgroundColor: 'rgba(25, 118, 210, 0.1)',
                      borderColor: 'rgba(25, 118, 210, 0.3)',
                      color: 'inherit',
                      '& .MuiChip-deleteIcon': {
                        color: 'inherit',
                        '&:hover': {
                          color: 'inherit',
                        }
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.2)',
                        borderColor: 'rgba(25, 118, 210, 0.5)',
                      }
                    }}
                  />
                ))
              )}
            </Box>
          </Box>
        </Box>
      </Collapse>

      {/* Minimized State Indicator */}
      {isMinimized && searchTerms.length > 0 && (
        <Box sx={{ px: 1.5, pb: 1.5 }}>
          <Chip 
            label={t('graph.activeCount', { count: searchTerms.length })}
            size="small" 
            color="primary" 
            variant="filled"
          />
        </Box>
      )}
    </Paper>
  );
};

export default SearchHighlightPanel;
